// src/components/subscription/UpgradeModal.tsx
import React from 'react';
import { X, Crown, Check } from 'lucide-react';
import { subscriptionApi } from '../../services/subscriptionApi';
import { useSubscription } from '../../contexts/SubscriptionContext';

const UpgradeModal: React.FC = () => {
  const { showUpgradeModal, setShowUpgradeModal, subscription, refresh } = useSubscription();
  const [isLoading, setIsLoading] = React.useState(false);
  
  if (!showUpgradeModal) {
    return null;
  }
  
  const handleUpgrade = async (planType: 'medium' | 'premium', paymentFrequency: 'monthly' | 'yearly') => {
    setIsLoading(true);
    try {
      const { url } = await subscriptionApi.createCheckoutSession(planType, paymentFrequency);
      setShowUpgradeModal(false);
      window.location.href = url;
    } catch (error) {
      console.error('Failed to start upgrade process:', error);
      // Optionally show an error message to the user here
      setIsLoading(false); // Only set loading false if redirect fails
    }
  };
  
  const currentPlan = subscription?.planType || 'free';
  
  return (
    // Overlay Style
    <div 
       className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-out"
       onClick={() => setShowUpgradeModal(false)} // Close on overlay click
    >
      {/* Modal Panel Style */} 
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
      >
        {/* Header Style */}
        <div className="flex justify-between items-center p-4 sm:p-5 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Upgrade Your Plan</h2>
          <button 
            onClick={() => setShowUpgradeModal(false)}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            disabled={isLoading}
            aria-label="Close upgrade modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content Area */} 
        <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Medium Plan Card */}
            <div className={`bg-white border rounded-lg flex flex-col ${currentPlan === 'medium' ? 'border-brand-primary ring-2 ring-brand-primary/50' : 'border-gray-200'}`}>
              <div className="p-5 border-b border-gray-200">
                 <div className="flex items-center gap-2 mb-2">
                   <Crown size={18} className="text-blue-600" />
                   <h3 className="text-lg font-semibold capitalize">Medium</h3>
                 </div>
                <p className="text-sm text-gray-600 mb-4 h-10">Everything you need for better relationships</p>
                <div className="flex items-baseline gap-4 mb-4">
                  <div className="flex-1 text-center">
                    <span className="text-3xl font-bold">$4.99</span>
                    <span className="text-sm text-gray-500">/mo</span>
                  </div>
                  <div className="flex-1 text-center border-l border-gray-200 pl-4">
                    <span className="text-3xl font-bold">$49</span>
                    <span className="text-sm text-gray-500">/yr</span>
                     <span className="block text-xs text-green-600 font-medium">(save 18%)</span>
                  </div>
                </div>
                
                 {/* Action Buttons */} 
                {currentPlan === 'medium' ? (
                  <button className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium cursor-default">Current Plan</button>
                 ) : currentPlan === 'premium' ? (
                   <button className="w-full bg-gray-100 text-gray-500 px-4 py-2 rounded-lg text-sm font-medium cursor-default">Premium Active</button>
                 ) : (
                  <div className="flex gap-2">
                    {/* Primary Monthly Button */}
                    <button
                      onClick={() => handleUpgrade('medium', 'monthly')}
                      className="flex-1 bg-brand-primary text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {isLoading ? '...' : 'Monthly'}
                    </button>
                    {/* Secondary Yearly Button */}
                    <button
                      onClick={() => handleUpgrade('medium', 'yearly')}
                      className="flex-1 bg-white text-brand-primary px-3 py-2 rounded-lg border border-brand-primary hover:bg-indigo-50 transition-colors text-sm font-medium disabled:opacity-50"
                      disabled={isLoading}
                    >
                       {isLoading ? '...' : 'Yearly'}
                    </button>
                  </div>
                )}
              </div>
              
              <div className="p-5 flex-grow">
                 <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">What's included:</h4>
                 <ul className="space-y-2">
                  <li className="flex items-start text-sm">
                    <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>100 contacts</span>{/* TODO: Get actual limit */} 
                  </li>
                  <li className="flex items-start text-sm">
                    <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>50 AI queries / month</span>{/* TODO: Get actual limit */} 
                  </li>
                  <li className="flex items-start text-sm">
                    <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Email & calendar integration</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Advanced analytics</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Premium Plan Card */}
            <div className={`bg-white border rounded-lg flex flex-col ${currentPlan === 'premium' ? 'border-purple-500 ring-2 ring-purple-500/50' : 'border-gray-200'}`}>
               <div className="p-5 border-b border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown size={18} className="text-purple-600" />
                    <h3 className="text-lg font-semibold capitalize">Premium</h3>
                  </div>
                 <p className="text-sm text-gray-600 mb-4 h-10">For professionals and power users</p>
                 <div className="flex items-baseline gap-4 mb-4">
                   <div className="flex-1 text-center">
                     <span className="text-3xl font-bold">$12.99</span>
                     <span className="text-sm text-gray-500">/mo</span>
                   </div>
                   <div className="flex-1 text-center border-l border-gray-200 pl-4">
                     <span className="text-3xl font-bold">$120</span>
                     <span className="text-sm text-gray-500">/yr</span>
                      <span className="block text-xs text-green-600 font-medium">(save 23%)</span>
                   </div>
                 </div>
                 
                 {/* Action Buttons */} 
                 {currentPlan === 'premium' ? (
                   <button className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium cursor-default">Current Plan</button>
                 ) : (
                   <div className="flex gap-2">
                     {/* Primary Monthly Button */} 
                     <button
                       onClick={() => handleUpgrade('premium', 'monthly')}
                       className="flex-1 bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50"
                       disabled={isLoading}
                     >
                       {isLoading ? '...' : 'Monthly'}
                     </button>
                      {/* Secondary Yearly Button */} 
                     <button
                       onClick={() => handleUpgrade('premium', 'yearly')}
                       className="flex-1 bg-white text-purple-600 px-3 py-2 rounded-lg border border-purple-500 hover:bg-purple-50 transition-colors text-sm font-medium disabled:opacity-50"
                       disabled={isLoading}
                     >
                        {isLoading ? '...' : 'Yearly'}
                     </button>
                   </div>
                 )}
               </div>
               
               <div className="p-5 flex-grow">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Everything in Medium, plus:</h4>
                  <ul className="space-y-2">
                   <li className="flex items-start text-sm">
                     <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                     <span>Unlimited contacts</span>
                   </li>
                   <li className="flex items-start text-sm">
                     <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                     <span>Unlimited AI queries</span>
                   </li>
                   <li className="flex items-start text-sm">
                     <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                     <span>Priority support</span>
                   </li>
                   <li className="flex items-start text-sm">
                     <Check size={14} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                     <span>Early access to new features</span>
                   </li>
                 </ul>
               </div>
            </div>
          </div>
        </div>
        
         {/* Footer Style */} 
         <div className="flex justify-end items-center gap-3 p-4 bg-gray-100 border-t border-gray-200 flex-shrink-0">
           <button
             type="button"
             onClick={() => setShowUpgradeModal(false)}
             className="bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
             disabled={isLoading}
           >
             Close
           </button>
         </div>
      </div>
    </div>
  );
};

export default UpgradeModal;